(() => {
  if (window.GlowbyHKCommerce) return;

  const STORAGE_KEY = 'glowbyhk:wishlist:v1';
  const RETURN_KEY = 'glowbyhk:product-return';
  const configElement = document.querySelector('[data-commerce-config]');
  const config = configElement?.dataset || {};
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let wishlist = [];
  let activeDialogOpener = null;

  const announce = (message) => {
    const status = document.querySelector('[data-commerce-status]');
    if (!status) return;
    status.textContent = '';
    requestAnimationFrame(() => { status.textContent = message; });
  };

  const parseWishlist = () => {
    try {
      const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      wishlist = Array.isArray(parsed) ? parsed.filter((item) => item?.id && item?.url && item?.title).slice(0, 100) : [];
    } catch {
      wishlist = [];
    }
  };

  const publish = (name, detail) => {
    try {
      window.Shopify?.analytics?.publish?.(`glowbyhk:${name}`, detail);
    } catch {
      // A configured Shopify pixel may subscribe; storefront behavior never depends on analytics.
    }
  };

  const openDialog = (dialog, opener) => {
    if (!(dialog instanceof HTMLDialogElement)) return;
    document.querySelectorAll('dialog[open]').forEach((open) => {
      if (open !== dialog) closeDialog(open, false);
    });
    activeDialogOpener = opener || document.activeElement;
    if (!dialog.open) dialog.showModal();
    requestAnimationFrame(() => {
      const closeControl = dialog.querySelector('[data-dialog-close], [data-cart-close]');
      (closeControl || dialog.querySelector('a, button'))?.focus();
    });
  };

  const closeDialog = (dialog, restoreFocus = true) => {
    if (!(dialog instanceof HTMLDialogElement) || !dialog.open) return;
    dialog.close();
    if (restoreFocus && activeDialogOpener?.isConnected) activeDialogOpener.focus();
    activeDialogOpener = null;
  };

  const productData = (source) => {
    const product = source?.closest?.('[data-wishlist-product]');
    if (!product) return null;
    const image = product.querySelector('.product-card__image, .quick-view-card__image, .product-media__image, img');
    const price = product.querySelector('.price, [class*="price"]');
    return {
      id: String(product.dataset.productId || ''),
      handle: product.dataset.productHandle || '',
      title: product.dataset.productTitle || '',
      family: product.dataset.productFamily || '',
      url: product.dataset.productUrl || '',
      image: image?.currentSrc || image?.src || '',
      imageAlt: image?.alt || product.dataset.productTitle || '',
      price: price?.textContent?.trim() || '',
    };
  };

  const saveWishlist = () => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(wishlist));
    } catch {
      // Private browsing/storage limits still allow the in-session UI to work.
    }
  };

  const refreshWishlistFromPage = (root = document) => {
    let changed = false;
    root.querySelectorAll?.('[data-wishlist-product]').forEach((product) => {
      const current = productData(product);
      const index = wishlist.findIndex((item) => String(item.id) === current?.id);
      if (index < 0 || !current?.image) return;
      const saved = wishlist[index];
      const updated = { ...saved, ...current };
      if (JSON.stringify(saved) !== JSON.stringify(updated)) {
        wishlist[index] = updated;
        changed = true;
      }
    });
    if (changed) saveWishlist();
  };

  const refreshWishlistProducts = async () => {
    const queue = wishlist.filter((item) => item.handle);
    if (!queue.length) return;
    const refreshed = new Map();
    const worker = async () => {
      while (queue.length) {
        const item = queue.shift();
        try {
          const root = config.rootUrl || '/';
          const response = await fetch(`${root}products/${encodeURIComponent(item.handle)}.js`, {
            headers: { Accept: 'application/json' },
          });
          if (!response.ok) continue;
          const product = await response.json();
          const image = typeof product.featured_image === 'string'
            ? product.featured_image
            : product.featured_image?.url || product.images?.[0];
          const imageUrl = image ? new URL(image, location.origin) : null;
          if (imageUrl?.hostname === 'cdn.shopify.com' || imageUrl?.pathname.includes('/cdn/shop/')) {
            imageUrl.searchParams.set('width', '360');
          }
          refreshed.set(String(item.id), {
            ...item,
            title: product.title || item.title,
            family: product.type || item.family,
            url: product.url || item.url,
            image: imageUrl?.href || item.image,
            price: Number.isFinite(Number(product.price)) ? formatMoney(Number(product.price)) : item.price,
          });
        } catch {
          // Keep the last saved payload if Shopify product JSON is temporarily unavailable.
        }
      }
    };
    await Promise.all(Array.from({ length: Math.min(4, queue.length) }, worker));
    if (!refreshed.size) return;
    wishlist = wishlist.map((item) => refreshed.get(String(item.id)) || item);
    saveWishlist();
  };

  const syncWishlistButtons = () => {
    const ids = new Set(wishlist.map((item) => String(item.id)));
    document.querySelectorAll('[data-wishlist-toggle]').forEach((button) => {
      const product = button.closest('[data-wishlist-product]');
      const saved = ids.has(String(product?.dataset.productId || ''));
      button.setAttribute('aria-pressed', String(saved));
      button.setAttribute('aria-label', saved ? button.dataset.removeLabel : button.dataset.addLabel);
    });
    document.querySelectorAll('[data-wishlist-count]').forEach((count) => {
      count.textContent = String(wishlist.length);
      count.dataset.empty = String(wishlist.length === 0);
    });
  };

  const renderWishlist = () => {
    const container = document.querySelector('[data-wishlist-items]');
    if (!container) return;
    container.replaceChildren();
    if (!wishlist.length) {
      const empty = document.createElement('p');
      empty.className = 'wishlist-dialog__empty';
      empty.textContent = config.wishlistEmpty || '';
      container.append(empty);
      return;
    }
    wishlist.forEach((item) => {
      const article = document.createElement('article');
      article.className = 'wishlist-item';
      if (item.image) {
        const media = document.createElement('a');
        media.className = 'wishlist-item__media';
        media.href = item.url;
        media.dataset.productLink = '';
        const image = document.createElement('img');
        image.src = item.image;
        image.alt = item.imageAlt || item.title;
        image.loading = 'lazy';
        media.append(image);
        article.append(media);
      }
      const content = document.createElement('div');
      content.className = 'wishlist-item__content';
      if (item.family) {
        const family = document.createElement('small');
        family.textContent = item.family;
        content.append(family);
      }
      const link = document.createElement('a');
      link.href = item.url;
      link.dataset.productLink = '';
      link.textContent = item.title;
      content.append(link);
      if (item.price) {
        const price = document.createElement('span');
        price.textContent = item.price;
        content.append(price);
      }
      article.append(content);
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'wishlist-item__remove';
      remove.dataset.wishlistRemove = item.id;
      remove.setAttribute('aria-label', `${config.wishlistRemove || ''}: ${item.title}`);
      const heartTemplate = document.querySelector('[data-heart-icon-template]');
      if (heartTemplate instanceof HTMLTemplateElement) remove.append(heartTemplate.content.cloneNode(true));
      article.append(remove);
      container.append(article);
    });
  };

  const toggleWishlist = (button) => {
    const item = productData(button);
    if (!item?.id) return;
    const index = wishlist.findIndex((saved) => String(saved.id) === item.id);
    if (index >= 0) {
      wishlist.splice(index, 1);
      publish('wishlist_removed', { product_id: item.id, product_handle: item.handle });
    } else {
      wishlist.unshift(item);
      publish('wishlist_added', { product_id: item.id, product_handle: item.handle });
    }
    saveWishlist();
    syncWishlistButtons();
    renderWishlist();
  };

  const getHeader = () => document.querySelector('glow-header');
  const getCartTarget = () => document.querySelector('.site-header [data-cart-open]');

  const historyBackContext = (historyBack) => {
    let storedUrl = '';
    try { storedUrl = sessionStorage.getItem(RETURN_KEY) || ''; } catch {}
    const candidates = [storedUrl, document.referrer].filter(Boolean);
    let returnUrl = '';
    for (const candidate of candidates) {
      try {
        const parsed = new URL(candidate, location.href);
        if (parsed.origin === location.origin && parsed.href !== location.href) {
          returnUrl = parsed.href;
          break;
        }
      } catch {}
    }

    let label = historyBack.dataset.backLabelPrevious || '';
    if (returnUrl) {
      const pathname = new URL(returnUrl).pathname;
      if (pathname === '/') label = historyBack.dataset.backLabelHome || label;
      else if (pathname.startsWith('/collections/')) label = historyBack.dataset.backLabelCollection || label;
      else if (pathname === '/search' || pathname.startsWith('/search/')) label = historyBack.dataset.backLabelSearch || label;
    } else if (history.length <= 1) {
      label = historyBack.dataset.backLabelHome || label;
    }
    return { label, returnUrl };
  };

  const syncHistoryBackLinks = () => {
    document.querySelectorAll('[data-history-back]').forEach((historyBack) => {
      const label = historyBack.querySelector('[data-history-back-label]');
      const context = historyBackContext(historyBack);
      if (label && context.label) label.textContent = context.label;
    });
  };

  const FLY_DURATION = 1050;

  const flyToCart = (source) => {
    if (reducedMotion.matches) return Promise.resolve();
    const image = source?.closest('[data-product-card], .quick-view-card, .product-main, .commerce-recommendation')?.querySelector('img');
    const target = getCartTarget();
    if (!image || !target) return Promise.resolve();
    const sourceRect = source?.getBoundingClientRect();
    const imageRect = image.getBoundingClientRect();
    const to = target.getBoundingClientRect();
    if (!sourceRect?.width || !imageRect.width) return Promise.resolve();
    const size = Math.min(imageRect.width, imageRect.height, 72);
    const startLeft = sourceRect.left + sourceRect.width / 2 - size / 2;
    const startTop = sourceRect.top + sourceRect.height / 2 - size / 2;
    const clone = image.cloneNode();
    clone.className = 'commerce-fly-image';
    Object.assign(clone.style, {
      top: `${startTop}px`,
      left: `${startLeft}px`,
      width: `${size}px`,
      height: `${size}px`,
    });
    document.body.append(clone);
    const x = to.left + to.width / 2 - startLeft - size / 2;
    const y = to.top + to.height / 2 - startTop - size / 2;
    const arcLift = Math.min(140, Math.max(56, Math.abs(y) * 0.16));
    const animation = clone.animate([
      { transform: 'translate3d(0, 0, 0) scale(1)', opacity: 0.96, offset: 0 },
      { transform: `translate3d(${x * 0.52}px, ${y * 0.42 - arcLift}px, 0) scale(0.66)`, opacity: 0.86, offset: 0.52 },
      { transform: `translate3d(${x}px, ${y}px, 0) scale(0.14)`, opacity: 0.08, offset: 1 },
    ], {
      duration: FLY_DURATION,
      easing: 'cubic-bezier(0.22, 0.72, 0.18, 1)',
      fill: 'forwards',
    });
    return animation.finished.catch(() => undefined).then(() => clone.remove());
  };

  const bumpCart = () => {
    const target = getCartTarget();
    if (!target) return;
    target.classList.remove('is-cart-bumped');
    requestAnimationFrame(() => target.classList.add('is-cart-bumped'));
    window.setTimeout(() => target.classList.remove('is-cart-bumped'), 450);
  };

  const replaceHeader = (html) => {
    if (!html) return false;
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const next = parsed.querySelector('glow-header');
    const current = getHeader();
    if (!next || !current) return false;
    current.replaceWith(next);
    syncWishlistButtons();
    scanRecommendations(next);
    return true;
  };

  const refreshHeader = async (sections) => {
    const header = getHeader();
    const sectionId = header?.dataset.sectionId;
    if (!sectionId) return;
    if (replaceHeader(sections?.[sectionId])) return;
    const root = config.rootUrl || '/';
    const response = await fetch(`${root}?section_id=${encodeURIComponent(sectionId)}`, { headers: { Accept: 'text/html' } });
    if (response.ok) replaceHeader(await response.text());
  };

  const openCart = (opener) => {
    const header = getHeader();
    const mobile = header?.querySelector('[data-mobile-dialog]');
    if (mobile?.open) mobile.close();
    const drawer = header?.querySelector('[data-cart-drawer]');
    openDialog(drawer, opener);
  };

  const sectionRequest = () => {
    const sectionId = getHeader()?.dataset.sectionId;
    return sectionId ? { sections: [sectionId], sections_url: window.location.pathname } : {};
  };

  const addVariant = async (variantId, quantity, source) => {
    if (!variantId) return;
    const button = source?.closest('button');
    button?.setAttribute('aria-busy', 'true');
    button?.setAttribute('disabled', '');
    const flight = flyToCart(source);
    try {
      const response = await fetch(`${config.rootUrl || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: Number(quantity) || 1 }], ...sectionRequest() }),
      });
      if (!response.ok) throw new Error('Cart add failed');
      const data = await response.json();
      await flight;
      await refreshHeader(data.sections);
      bumpCart();
      announce(config.addedText || '');
      publish('ajax_cart_added', { variant_id: String(variantId), quantity: Number(quantity) || 1 });
    } catch {
      announce(config.errorText || '');
      button?.removeAttribute('disabled');
    } finally {
      if (button?.isConnected) {
        button.removeAttribute('aria-busy');
        button.removeAttribute('disabled');
      }
    }
  };

  const changeCartLine = async (lineKey, quantity, source) => {
    if (!lineKey) return;
    source?.setAttribute('disabled', '');
    try {
      const response = await fetch(`${config.rootUrl || '/'}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity: Math.max(0, Number(quantity) || 0), ...sectionRequest() }),
      });
      if (!response.ok) throw new Error('Cart update failed');
      const data = await response.json();
      await refreshHeader(data.sections);
      openCart(getCartTarget());
    } catch {
      announce(config.errorText || '');
      source?.removeAttribute('disabled');
    } finally {
      if (source?.isConnected) source.removeAttribute('disabled');
    }
  };

  const formatMoney = (cents) => {
    const value = Number(cents) / 100;
    if (!config.currency) return value.toLocaleString(document.documentElement.lang || 'en');
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: config.currency }).format(value);
    } catch {
      return value.toFixed(2);
    }
  };

  const recommendationProducts = async (productId, intent) => {
    const root = config.rootUrl || '/';
    const url = `${root}recommendations/products.json?product_id=${encodeURIComponent(productId)}&limit=3&intent=${intent}`;
    const response = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!response.ok) return [];
    const data = await response.json();
    return Array.isArray(data.products) ? data.products : [];
  };

  const renderRecommendations = (shell, products) => {
    shell.replaceChildren();
    if (!products.length) {
      const empty = document.createElement('p');
      empty.className = 'commerce-recommendations__status';
      empty.textContent = shell.dataset.emptyText || '';
      shell.append(empty);
      return;
    }
    const heading = document.createElement('h3');
    heading.textContent = shell.dataset.heading || '';
    shell.append(heading);
    const list = document.createElement('div');
    list.className = 'commerce-recommendations__list';
    products.forEach((product) => {
      const variant = product.variants?.find((item) => item.available && Number(item.price) > 0);
      if (!variant) return;
      const article = document.createElement('article');
      article.className = 'commerce-recommendation';
      const imageUrl = typeof product.featured_image === 'string'
        ? product.featured_image
        : product.featured_image?.url || product.images?.[0];
      if (imageUrl) {
        const media = document.createElement('a');
        media.className = 'commerce-recommendation__media';
        media.href = product.url;
        media.dataset.productLink = '';
        const image = document.createElement('img');
        image.src = imageUrl;
        image.alt = product.title;
        image.loading = 'lazy';
        media.append(image);
        article.append(media);
      }
      const content = document.createElement('div');
      content.className = 'commerce-recommendation__content';
      const link = document.createElement('a');
      link.href = product.url;
      link.dataset.productLink = '';
      link.textContent = product.title;
      const price = document.createElement('span');
      price.textContent = formatMoney(variant.price);
      content.append(link, price);
      article.append(content);
      const add = document.createElement('button');
      add.type = 'button';
      add.className = 'commerce-recommendation__add';
      add.dataset.productAdd = '';
      add.dataset.variantId = variant.id;
      add.setAttribute('aria-label', `${shell.dataset.addLabel || ''}: ${product.title}`);
      const addIconTemplate = document.querySelector('[data-add-icon-template]');
      if (addIconTemplate instanceof HTMLTemplateElement) add.append(addIconTemplate.content.cloneNode(true));
      article.append(add);
      list.append(article);
    });
    if (list.children.length) shell.append(list);
  };

  const loadRecommendations = async (shell) => {
    if (shell.dataset.loaded === 'true') return;
    shell.dataset.loaded = 'true';
    try {
      let products = await recommendationProducts(shell.dataset.productId, 'complementary');
      if (!products.length) products = await recommendationProducts(shell.dataset.productId, 'related');
      renderRecommendations(shell, products);
    } catch {
      renderRecommendations(shell, []);
    }
  };

  const recommendationObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries, observer) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        observer.unobserve(entry.target);
        loadRecommendations(entry.target);
      });
    }, { rootMargin: '180px' })
    : null;

  function scanRecommendations(root = document) {
    root.querySelectorAll?.('[data-product-recommendations]').forEach((shell) => {
      if (recommendationObserver) recommendationObserver.observe(shell);
      else loadRecommendations(shell);
    });
  }

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (target.matches('dialog[data-cart-drawer], dialog[data-wishlist-dialog], dialog[data-quick-view-dialog]')) {
      closeDialog(target);
      return;
    }
    const cartOpen = target.closest('[data-cart-open]');
    if (cartOpen) {
      event.preventDefault();
      openCart(cartOpen);
      return;
    }
    const cartClose = target.closest('[data-cart-close]');
    if (cartClose) {
      closeDialog(cartClose.closest('dialog'));
      return;
    }
    const dialogClose = target.closest('[data-dialog-close]');
    if (dialogClose) {
      closeDialog(dialogClose.closest('dialog'));
      return;
    }
    const wishlistOpen = target.closest('[data-wishlist-open]');
    if (wishlistOpen) {
      refreshWishlistFromPage();
      renderWishlist();
      openDialog(document.querySelector('[data-wishlist-dialog]'), wishlistOpen);
      refreshWishlistProducts().then(() => {
        syncWishlistButtons();
        renderWishlist();
      });
      return;
    }
    const wishlistToggle = target.closest('[data-wishlist-toggle]');
    if (wishlistToggle) {
      toggleWishlist(wishlistToggle);
      return;
    }
    const wishlistRemove = target.closest('[data-wishlist-remove]');
    if (wishlistRemove) {
      const index = wishlist.findIndex((item) => String(item.id) === wishlistRemove.dataset.wishlistRemove);
      if (index >= 0) {
        const [item] = wishlist.splice(index, 1);
        saveWishlist();
        publish('wishlist_removed', { product_id: item.id, product_handle: item.handle });
        syncWishlistButtons();
        renderWishlist();
      }
      return;
    }
    const quickOpen = target.closest('[data-quick-view-open]');
    if (quickOpen) {
      const template = quickOpen.closest('[data-product-card]')?.querySelector('[data-quick-view-template]');
      const dialog = document.querySelector('[data-quick-view-dialog]');
      const container = dialog?.querySelector('[data-quick-view-content]');
      if (template && container) {
        container.replaceChildren(template.content.cloneNode(true));
        syncWishlistButtons();
        openDialog(dialog, quickOpen);
      }
      return;
    }
    const cartChange = target.closest('[data-cart-change]');
    if (cartChange) {
      changeCartLine(cartChange.dataset.lineKey, cartChange.dataset.quantity, cartChange);
      return;
    }
    const productAdd = target.closest('[data-product-add][data-variant-id]');
    if (productAdd) {
      event.preventDefault();
      if (productAdd.dataset.soldOut === 'true') {
        productAdd.classList.add('is-sold-out-feedback');
        announce(productAdd.dataset.soldOutText || config.soldOutText || '');
        window.setTimeout(() => productAdd.classList.remove('is-sold-out-feedback'), 1800);
        return;
      }
      if (productAdd.closest('[data-product-form]')) {
        productAdd.closest('form')?.requestSubmit(productAdd);
        return;
      }
      addVariant(productAdd.dataset.variantId, 1, productAdd);
      return;
    }
    const productLink = target.closest('[data-product-link]');
    if (productLink) {
      try { sessionStorage.setItem(RETURN_KEY, window.location.href); } catch {}
      return;
    }
    const historyBack = target.closest('[data-history-back]');
    if (historyBack) {
      event.preventDefault();
      const context = historyBackContext(historyBack);
      try { sessionStorage.removeItem(RETURN_KEY); } catch {}
      if (context.returnUrl) location.href = context.returnUrl;
      else if (history.length > 1) history.back();
      else location.href = historyBack.href;
    }
  });

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.closest('[data-product-form]')) return;
    if (event.submitter && !event.submitter.matches('[data-product-add]')) return;
    event.preventDefault();
    const data = new FormData(form);
    addVariant(data.get('id'), data.get('quantity') || 1, form.querySelector('[data-product-add]'));
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const openDialogs = Array.from(document.querySelectorAll('dialog[open]'));
    const dialog = openDialogs.at(-1);
    if (!dialog) return;
    event.preventDefault();
    closeDialog(dialog);
  });

  document.querySelectorAll('dialog').forEach((dialog) => {
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) closeDialog(dialog);
    });
  });

  parseWishlist();
  refreshWishlistFromPage();
  syncHistoryBackLinks();
  syncWishlistButtons();
  renderWishlist();
  scanRecommendations();

  window.GlowbyHKCommerce = { scanRecommendations, syncWishlistButtons };
})();
